import { useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { companiesHouseApi, FilingRequest } from "../api/companiesHouse";
import { hmrcVatApi, VatReturnReq } from "../api/hmrcVat";

export function TaxCompliance() {
  const [companyNumber, setCompanyNumber] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ["ch-status"],
    queryFn: companiesHouseApi.getStatus,
  });

  const { data: companyData, isLoading: companyLoading, refetch: lookupCompany } = useQuery({
    queryKey: ["ch-company", companyNumber],
    queryFn: () => companiesHouseApi.lookupCompany(companyNumber),
    enabled: false,
  });

  const { data: searchResults, isLoading: searchLoading, refetch: searchCompanies } = useQuery({
    queryKey: ["ch-search", searchQuery],
    queryFn: () => companiesHouseApi.searchCompanies(searchQuery),
    enabled: false,
  });

  // Test filing state
  const [filing, setFiling] = useState<FilingRequest>({
    companyNumber: "",
    companyName: "",
    companyType: "EW",
    companyAuthCode: "",
    accountsBase64: "",
    formType: "Accounts",
    dateSigned: new Date().toISOString().split("T")[0],
    contactName: "OTC",
    contactNumber: "+44 0000000000",
  });
  const [filingResult, setFilingResult] = useState<any>(null);
  const [filingLoading, setFilingLoading] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  // --- HMRC VAT state ---
  const [vrn, setVrn] = useState("");
  const [vatFrom, setVatFrom] = useState("");
  const [vatTo, setVatTo] = useState("");
  const [manualToken, setManualToken] = useState("");
  const [vatReturn, setVatReturn] = useState<VatReturnReq>({
    periodKey: "",
    vatDueSales: 0,
    vatDueAcquisitions: 0,
    totalVatDue: 0,
    vatReclaimedCurrPeriod: 0,
    netVatDue: 0,
    totalValueSalesExVAT: 0,
    totalValuePurchasesExVAT: 0,
    totalValueGoodsSuppliedExVAT: 0,
    totalAcquisitionsExVAT: 0,
    finalised: true,
  });
  const [vatResult, setVatResult] = useState<any>(null);
  const [vatLoading, setVatLoading] = useState(false);

  const { data: hmrcStatus, isLoading: hmrcStatusLoading } = useQuery({
    queryKey: ["hmrc-vat-status"],
    queryFn: hmrcVatApi.getStatus,
  });

  const handleVatAction = async (fn: () => Promise<any>) => {
    setVatLoading(true);
    setVatResult(null);
    try {
      const result = await fn();
      setVatResult(result);
    } catch (err: any) {
      setVatResult({ error: err.message || String(err) });
    } finally {
      setVatLoading(false);
    }
  };

  const updateVatField = (key: keyof VatReturnReq, value: string | number | boolean) => {
    setVatReturn((prev) => ({ ...prev, [key]: value }));
  };

  const updateField = (key: keyof FilingRequest, value: string) => {
    setFiling((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1] ?? "";
      updateField("accountsBase64", base64);
    };
    reader.readAsDataURL(file);
  };

  const canSubmit =
    filing.companyNumber &&
    filing.companyName &&
    filing.companyAuthCode &&
    filing.accountsBase64;

  const submit = async (validateOnly: boolean) => {
    if (!canSubmit) return;
    setFilingLoading(true);
    setFilingResult(null);
    try {
      const api = validateOnly
        ? companiesHouseApi.validateFiling
        : companiesHouseApi.fileAccounts;
      const result = await api(filing);
      setFilingResult(result);
      if (!validateOnly && result?.submissionNumber) {
        setHistory((prev) => [
          {
            submissionNumber: result.submissionNumber,
            submissionId: result.submissionId,
            status: result.status,
            at: new Date().toISOString(),
          },
          ...prev,
        ]);
      }
    } catch (err: any) {
      setFilingResult({ error: err.message || String(err) });
    } finally {
      setFilingLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Tax & Compliance</h1>

      {/* Status Badge */}
      <div className="mb-6 p-4 bg-card rounded-lg border">
        <h2 className="text-lg font-semibold mb-2">Companies House Connection</h2>
        {statusLoading ? (
          <span className="text-muted-foreground">Checking...</span>
        ) : status?.configured ? (
          <span className="text-green-600 font-medium">
            Connected (Presenter: {status.presenterId})
          </span>
        ) : (
          <span className="text-red-500 font-medium">
            Not configured — add CH_PRESENTER_ID and CH_PRESENTER_CODE to backend env
          </span>
        )}
      </div>

      {/* Company Lookup */}
      <div className="mb-6 p-4 bg-card rounded-lg border">
        <h2 className="text-lg font-semibold mb-3">Company Lookup</h2>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            placeholder="Enter company number (e.g. 00000006)"
            value={companyNumber}
            onChange={(e) => setCompanyNumber(e.target.value)}
            className="flex-1 px-3 py-2 border rounded-md bg-background"
          />
          <button
            onClick={() => lookupCompany()}
            disabled={companyLoading || !companyNumber}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            {companyLoading ? "Loading..." : "Lookup"}
          </button>
        </div>

        {companyData && (
          <div className="space-y-2 text-sm">
            <div className="flex justify-between border-b pb-2">
              <span className="text-muted-foreground">Company Name</span>
              <span className="font-medium">{companyData.company_name}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-muted-foreground">Number</span>
              <span className="font-medium">{companyData.company_number}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-muted-foreground">Status</span>
              <span className="font-medium capitalize">{companyData.company_status}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="text-muted-foreground">Incorporated</span>
              <span className="font-medium">{companyData.date_of_creation}</span>
            </div>
            {companyData.registered_office_address && (
              <div className="flex justify-between border-b pb-2">
                <span className="text-muted-foreground">Address</span>
                <span className="font-medium text-right">
                  {[
                    companyData.registered_office_address.address_line_1,
                    companyData.registered_office_address.locality,
                    companyData.registered_office_address.postal_code,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            )}
            {companyData.sic_codes && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">SIC Codes</span>
                <span className="font-medium">{companyData.sic_codes.join(", ")}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Company Search */}
      <div className="mb-6 p-4 bg-card rounded-lg border">
        <h2 className="text-lg font-semibold mb-3">Search Companies</h2>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-3 py-2 border rounded-md bg-background"
          />
          <button
            onClick={() => searchCompanies()}
            disabled={searchLoading || !searchQuery}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            {searchLoading ? "Loading..." : "Search"}
          </button>
        </div>

        {searchResults?.items && (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {searchResults.items.map((item: any) => (
              <div
                key={item.company_number}
                className="p-3 border rounded-md hover:bg-accent cursor-pointer"
                onClick={() => {
                  setCompanyNumber(item.company_number);
                  lookupCompany();
                }}
              >
                <div className="font-medium">{item.title}</div>
                <div className="text-xs text-muted-foreground">
                  {item.company_number} · {item.company_status}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Test Filing */}
      <div className="p-4 bg-card rounded-lg border">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold">Test File Accounts</h2>
          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">Sandbox</span>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Submit test accounts to Companies House. Uses test presenter credentials and the
          official test XML envelope. Submission numbers are unique and incremental.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <input
            type="text"
            placeholder="Company number"
            value={filing.companyNumber}
            onChange={(e) => updateField("companyNumber", e.target.value)}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="text"
            placeholder="Company name"
            value={filing.companyName}
            onChange={(e) => updateField("companyName", e.target.value)}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="text"
            placeholder="Company type (e.g. EW)"
            value={filing.companyType}
            onChange={(e) => updateField("companyType", e.target.value)}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="text"
            placeholder="Company auth code"
            value={filing.companyAuthCode}
            onChange={(e) => updateField("companyAuthCode", e.target.value)}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="text"
            placeholder="Form type"
            value={filing.formType}
            onChange={(e) => updateField("formType", e.target.value)}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="date"
            placeholder="Date signed"
            value={filing.dateSigned}
            onChange={(e) => updateField("dateSigned", e.target.value)}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="text"
            placeholder="Contact name"
            value={filing.contactName}
            onChange={(e) => updateField("contactName", e.target.value)}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="text"
            placeholder="Contact number"
            value={filing.contactNumber}
            onChange={(e) => updateField("contactNumber", e.target.value)}
            className="px-3 py-2 border rounded-md bg-background"
          />
        </div>

        <div className="mb-4">
          <input
            ref={fileRef}
            type="file"
            accept=".html,.xhtml,.ixbrl"
            onChange={handleFileChange}
            className="block w-full text-sm text-muted-foreground file:mr-4 file:px-4 file:py-2 file:bg-primary file:text-primary-foreground file:rounded-md file:border-0"
          />
          {filing.accountsBase64 && (
            <p className="text-xs text-green-600 mt-2">
              iXBRL loaded ({Math.round(filing.accountsBase64.length / 1024)} KB)
            </p>
          )}
        </div>

        <div className="flex gap-2 mb-4">
          <button
            onClick={() => submit(false)}
            disabled={filingLoading || !canSubmit}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            {filingLoading ? "Submitting..." : "Submit Test Filing"}
          </button>
          <button
            onClick={() => submit(true)}
            disabled={filingLoading || !canSubmit}
            className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 disabled:opacity-50"
          >
            Validate
          </button>
        </div>

        {filingResult && (
          <div className="mb-4 p-3 bg-muted rounded-md text-sm">
            <pre className="whitespace-pre-wrap break-words">
              {JSON.stringify(filingResult, null, 2)}
            </pre>
          </div>
        )}

        {history.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold mb-2">Submission log</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {history.map((h, i) => (
                <div
                  key={i}
                  className="text-xs p-2 border rounded-md flex justify-between"
                >
                  <span>Submission #{h.submissionNumber}</span>
                  <span className="text-muted-foreground">{h.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* HMRC VAT Connection */}
      <div className="mb-6 p-4 bg-card rounded-lg border">
        <h2 className="text-lg font-semibold mb-2">HMRC VAT Connection</h2>
        {hmrcStatusLoading ? (
          <span className="text-muted-foreground">Checking...</span>
        ) : hmrcStatus?.configured ? (
          <div className="space-y-1">
            <span className="text-green-600 font-medium block">
              Configured
            </span>
            <span className="text-sm">
              Authenticated: {hmrcStatus.authenticated ? "Yes" : "No"}
            </span>
          </div>
        ) : (
          <span className="text-red-500 font-medium">
            Not configured — add HMRC_CLIENT_ID and HMRC_CLIENT_SECRET to backend env
          </span>
        )}
      </div>

      {/* HMRC OAuth */}
      <div className="mb-6 p-4 bg-card rounded-lg border">
        <h2 className="text-lg font-semibold mb-3">HMRC Authentication</h2>
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => handleVatAction(async () => {
              const { url } = await hmrcVatApi.getAuthUrl();
              window.open(url, "_blank");
              return { message: "Opened HMRC auth URL in new tab. After authorizing, paste the access token below." };
            })}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Get Auth URL
          </button>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Paste access token..."
            value={manualToken}
            onChange={(e) => setManualToken(e.target.value)}
            className="flex-1 px-3 py-2 border rounded-md bg-background text-sm"
          />
          <button
            onClick={() => handleVatAction(() => hmrcVatApi.setToken(manualToken))}
            disabled={!manualToken || vatLoading}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            Set Token
          </button>
        </div>
      </div>

      {/* VAT Obligations */}
      <div className="mb-6 p-4 bg-card rounded-lg border">
        <h2 className="text-lg font-semibold mb-3">VAT Obligations</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <input
            type="text"
            placeholder="VRN (e.g. 123456789)"
            value={vrn}
            onChange={(e) => setVrn(e.target.value)}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="date"
            placeholder="From"
            value={vatFrom}
            onChange={(e) => setVatFrom(e.target.value)}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="date"
            placeholder="To"
            value={vatTo}
            onChange={(e) => setVatTo(e.target.value)}
            className="px-3 py-2 border rounded-md bg-background"
          />
        </div>
        <button
          onClick={() => handleVatAction(() => hmrcVatApi.getObligations(vrn, vatFrom, vatTo))}
          disabled={vatLoading || !vrn || !vatFrom || !vatTo}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
        >
          {vatLoading ? "Loading..." : "Get Obligations"}
        </button>
      </div>

      {/* Submit VAT Return */}
      <div className="mb-6 p-4 bg-card rounded-lg border">
        <h2 className="text-lg font-semibold mb-3">Submit VAT Return</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <input
            type="text"
            placeholder="Period Key"
            value={vatReturn.periodKey}
            onChange={(e) => updateVatField("periodKey", e.target.value)}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="number"
            placeholder="VAT due on sales"
            value={vatReturn.vatDueSales}
            onChange={(e) => updateVatField("vatDueSales", parseFloat(e.target.value || "0"))}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="number"
            placeholder="VAT due on acquisitions"
            value={vatReturn.vatDueAcquisitions}
            onChange={(e) => updateVatField("vatDueAcquisitions", parseFloat(e.target.value || "0"))}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="number"
            placeholder="Total VAT due"
            value={vatReturn.totalVatDue}
            onChange={(e) => updateVatField("totalVatDue", parseFloat(e.target.value || "0"))}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="number"
            placeholder="VAT reclaimed (current period)"
            value={vatReturn.vatReclaimedCurrPeriod}
            onChange={(e) => updateVatField("vatReclaimedCurrPeriod", parseFloat(e.target.value || "0"))}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="number"
            placeholder="Net VAT due"
            value={vatReturn.netVatDue}
            onChange={(e) => updateVatField("netVatDue", parseFloat(e.target.value || "0"))}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="number"
            placeholder="Total sales (ex VAT)"
            value={vatReturn.totalValueSalesExVAT}
            onChange={(e) => updateVatField("totalValueSalesExVAT", parseInt(e.target.value || "0", 10))}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="number"
            placeholder="Total purchases (ex VAT)"
            value={vatReturn.totalValuePurchasesExVAT}
            onChange={(e) => updateVatField("totalValuePurchasesExVAT", parseInt(e.target.value || "0", 10))}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="number"
            placeholder="Total goods supplied (ex VAT)"
            value={vatReturn.totalValueGoodsSuppliedExVAT}
            onChange={(e) => updateVatField("totalValueGoodsSuppliedExVAT", parseInt(e.target.value || "0", 10))}
            className="px-3 py-2 border rounded-md bg-background"
          />
          <input
            type="number"
            placeholder="Total acquisitions (ex VAT)"
            value={vatReturn.totalAcquisitionsExVAT}
            onChange={(e) => updateVatField("totalAcquisitionsExVAT", parseInt(e.target.value || "0", 10))}
            className="px-3 py-2 border rounded-md bg-background"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleVatAction(() => hmrcVatApi.submitReturn(vrn, vatReturn))}
            disabled={vatLoading || !vrn || !vatReturn.periodKey}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
          >
            {vatLoading ? "Submitting..." : "Submit VAT Return"}
          </button>
          <button
            onClick={() => handleVatAction(() => hmrcVatApi.testFraudHeaders())}
            disabled={vatLoading}
            className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 disabled:opacity-50"
          >
            Test Fraud Headers
          </button>
        </div>
      </div>

      {/* VAT Result */}
      {vatResult && (
        <div className="mb-6 p-3 bg-muted rounded-md text-sm">
          <pre className="whitespace-pre-wrap break-words">
            {JSON.stringify(vatResult, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
